import * as XLSX from 'xlsx';
import type { ConsignmentReconciliationResponse } from '../types/venue';
import { fromMinorUnitsOrNull } from './money';

// ── Helpers ───────────────────────────────────────────────────────────────────

const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const fmtSize = (v?: number | null, u?: string | null): string => {
  if (!v) return '';
  return u ? `${v} ${u}` : `${v}`;
};

const safeName = (s: string): string =>
  s.replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-').slice(0, 30);

// ── Excel export ──────────────────────────────────────────────────────────────

export function downloadConsignmentReportExcel(
  report: ConsignmentReconciliationResponse,
): void {
  const { header, openingStock, movementsInPeriod, closingStock, reconciliationCheck, payableEstimate } = report;

  const wb = XLSX.utils.book_new();

  // ── Sheet 1: Summary (key-value layout) ────────────────────────────────────
  const summaryData: (string | number)[][] = [
    ['Report Type',  'Consignment Reconciliation'],
    ['Consignor',    header.supplier?.name ?? '—'],
    ['Account',      header.ownershipAccount.name],
    ['Period From',  fmtDate(header.periodFrom)],
    ['Period To',    fmtDate(header.periodTo)],
    ['Generated',    fmtDate(header.generatedAt)],
    [],
    ['DATA LIMITATION', header.dataLimitation.replace(/_/g, ' ')],
    ['Note', 'POS sales are not attributed per consignment account. This report is a stock reconciliation only, not a payable settlement.'],
    [],
    ['STOCK SUMMARY', ''],
    ['Opening Stock (units)', openingStock.totalQuantity],
    ['Stock In  (total received, units)', movementsInPeriod.totalIn],
    ['Stock Out (total depleted, units)', movementsInPeriod.totalOut],
    ['Closing Stock (units)', closingStock.totalQuantity],
    ['Reconciled', reconciliationCheck.reconciled ? 'Yes' : `No (variance: ${reconciliationCheck.variance})`],
    [],
    ['COST BASIS (informational only)', ''],
    ['Current Stock Cost Basis', payableEstimate.partialCostBasis ?? 'N/A — no lot cost data available'],
    ['Payable Estimate', 'NOT AVAILABLE — ' + payableEstimate.reason],
    [],
    ['MOVEMENTS BY TYPE', ''],
    ...Object.entries(movementsInPeriod.byType).map(([type, qty]) => [`  ${type}`, qty]),
  ];

  const summaryWs = XLSX.utils.aoa_to_sheet(summaryData);
  summaryWs['!cols'] = [{ wch: 38 }, { wch: 70 }];
  XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');

  // ── Sheet 2: Closing Stock Detail ─────────────────────────────────────────
  type StockRow = Record<string, string | number>;
  const stockRows: StockRow[] = closingStock.items.map(item => ({
    'Item':          item.displayName ?? '—',
    'Size':          fmtSize(item.sizeValue, item.sizeUnit),
    'Lot':           item.lotNumber ?? '—',
    'Batch Ref':     item.supplierBatchRef ?? '—',
    'Received Date': fmtDate(item.receivedDate),
    'Expiry Date':   fmtDate(item.expiryDate),
    'Location':      item.locationName ?? '—',
    'Quantity':      item.quantity,
    'Unit Cost':     fromMinorUnitsOrNull(item.unitCostAtReceipt) ?? '',
  }));

  if (stockRows.length === 0) {
    stockRows.push({
      'Item': '(no stock on hand)',
      'Size': '', 'Lot': '', 'Batch Ref': '',
      'Received Date': '', 'Expiry Date': '',
      'Location': '', 'Quantity': 0, 'Unit Cost': '',
    });
  }

  const stockWs = XLSX.utils.json_to_sheet(stockRows);
  stockWs['!cols'] = [
    { wch: 40 }, { wch: 10 }, { wch: 16 }, { wch: 16 },
    { wch: 14 }, { wch: 14 }, { wch: 22 }, { wch: 10 }, { wch: 12 },
  ];
  XLSX.utils.book_append_sheet(wb, stockWs, 'Closing Stock');

  // ── Sheet 3: Movements ────────────────────────────────────────────────────
  type MovRow = Record<string, string | number>;
  const movRows: MovRow[] = movementsInPeriod.rows.map(m => ({
    'Date':     fmtDate(m.postedAt),
    'Type':     m.type,
    'Item':     m.inventoryItem.displayName ?? '—',
    'Size':     fmtSize(m.inventoryItem.sizeValue, m.inventoryItem.sizeUnit),
    'Lot':      m.lot?.lotNumber ?? '—',
    'Location': m.location?.name ?? '—',
    'Qty In':   m.quantity > 0 ? m.quantity : '',
    'Qty Out':  m.quantity < 0 ? Math.abs(m.quantity) : '',
    'Source':   m.sourceDocument?.type ?? '—',
    'Notes':    m.notes ?? '',
  }));

  if (movementsInPeriod.truncated) {
    movRows.unshift({
      'Date': `NOTE: Showing first 1000 of ${movementsInPeriod.totalMovements} movements`,
      'Type': '', 'Item': '', 'Size': '', 'Lot': '',
      'Location': '', 'Qty In': '', 'Qty Out': '', 'Source': '', 'Notes': '',
    });
  }

  if (movRows.length === 0) {
    movRows.push({
      'Date': '(no movements in period)',
      'Type': '', 'Item': '', 'Size': '', 'Lot': '',
      'Location': '', 'Qty In': '', 'Qty Out': '', 'Source': '', 'Notes': '',
    });
  }

  const movWs = XLSX.utils.json_to_sheet(movRows);
  movWs['!cols'] = [
    { wch: 14 }, { wch: 18 }, { wch: 36 }, { wch: 10 },
    { wch: 14 }, { wch: 20 }, { wch: 9 }, { wch: 9 }, { wch: 16 }, { wch: 24 },
  ];
  XLSX.utils.book_append_sheet(wb, movWs, 'Movements');

  // ── Write file ────────────────────────────────────────────────────────────
  const datePart = header.periodTo.slice(0, 10);
  XLSX.writeFile(wb, `Consignment-${safeName(header.ownershipAccount.name)}-${datePart}.xlsx`);
}
